import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Work as WorkIcon,
  Settings as OperationIcon,
  Assignment as SerialIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  createNCRStart,
  fetchDropdownsStart,
  setSelectedWorkOrderId,
  setSelectedOperationId,
  clearError,
  setValidationErrors,
  clearValidationErrors,
  resetNCRState,
} from '../../store/reducers/ncrSlice';
import { CreateNCRRequest, NCRSerial } from '../../services/ncrApi';
import { ncrApi } from '../../services/ncrApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 'bold',
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  cardIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  serialChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  noSerials: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  validationError: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
  },
}));

const NCREdit: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = Boolean(id);

  const {
    dropdowns,
    dropdownsLoading,
    selectedWorkOrderId,
    selectedOperationId,
    createNCRLoading,
    createNCRError,
    validationErrors,
  } = useSelector((state: RootState) => state.ncr);

  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [availableSerials, setAvailableSerials] = useState<NCRSerial[]>([]);

  useEffect(() => {
    dispatch(resetNCRState());
    dispatch(fetchDropdownsStart());
    
    return () => {
      dispatch(clearValidationErrors());
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    // Fetch serials when work order and operation are selected
    if (selectedWorkOrderId && selectedOperationId) {
      fetchSerialsForSelection();
    } else {
      setAvailableSerials([]);
      setSelectedSerials([]);
    }
  }, [selectedWorkOrderId, selectedOperationId]);

  const fetchSerialsForSelection = async () => {
    if (!selectedWorkOrderId) return;
    
    try {
      const serials = await ncrApi.getSerialsByWorkOrder(selectedWorkOrderId, selectedOperationId || undefined);
      setAvailableSerials(serials);
    } catch (error) {
      console.error('Failed to fetch serials:', error);
      setAvailableSerials([]);
    }
  };

  const handleWorkOrderChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const workOrderId = event.target.value as string;
    dispatch(setSelectedWorkOrderId(workOrderId || null));
    setSelectedSerials([]);
  };

  const handleOperationChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const operationId = event.target.value as string;
    dispatch(setSelectedOperationId(operationId || null));
    setSelectedSerials([]);
  };

  const handleSerialToggle = (serialNum: string) => {
    setSelectedSerials(prev => 
      prev.includes(serialNum)
        ? prev.filter(s => s !== serialNum)
        : [...prev, serialNum]
    );
  };

  const handleSave = async () => {
    // Clear previous validation errors
    dispatch(clearValidationErrors());

    // Create request data
    const createData: CreateNCRRequest = {
      work_order_id: selectedWorkOrderId || '',
      operation_id: selectedOperationId || '',
      serialData: selectedSerials.map(serialNum => ({
        serial_num: serialNum,
        serial_status_id: 1 // Default status
      }))
    };

    // Client-side validation
    const errors: string[] = [];
    if (!createData.work_order_id) {
      errors.push('Work Order is required');
    }
    if (!createData.operation_id) {
      errors.push('Operation is required');
    }
    if (!createData.serialData || createData.serialData.length === 0) {
      errors.push('At least one serial must be selected');
    }

    if (errors.length > 0) {
      dispatch(setValidationErrors(errors));
      return;
    }

    // Create the NCR
    dispatch(createNCRStart(createData));
  };

  const handleCancel = () => {
    navigate('/reports/ncr');
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  // Get selected work order info
  const selectedWorkOrder = dropdowns.workOrders.find(wo => wo.id === selectedWorkOrderId);
  const selectedOperation = dropdowns.operations.find(op => op.id === selectedOperationId);

  // Filter operations for selected work order
  const filteredOperations = dropdowns.operations.filter(op => 
    !selectedWorkOrderId || op.route_card_id === selectedWorkOrder?.route_card?.id
  );

  if (dropdownsLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          {isEditMode ? 'Edit NCR' : 'Create New NCR'}
        </Typography>
      </Box>

      {/* Work Order Selection */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <WorkIcon className={classes.cardIcon} />
            <Typography variant="h6">Work Order Selection</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                variant="outlined" 
                className={classes.formControl}
                error={validationErrors.some(error => error.includes('Work Order'))}
              >
                <InputLabel>Work Order</InputLabel>
                <Select
                  value={selectedWorkOrderId || ''}
                  onChange={handleWorkOrderChange}
                  label="Work Order"
                  required
                >
                  <MenuItem value="">
                    <em>Select a Work Order</em>
                  </MenuItem>
                  {dropdowns.workOrders.map((workOrder) => (
                    <MenuItem key={workOrder.id} value={workOrder.id}>
                      {workOrder.wo_num} - {workOrder.part?.description || 'No description'}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.some(error => error.includes('Work Order')) && (
                  <FormHelperText>Work Order is required</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                variant="outlined" 
                className={classes.formControl}
                disabled={!selectedWorkOrderId}
                error={validationErrors.some(error => error.includes('Operation'))}
              >
                <InputLabel>Operation</InputLabel>
                <Select
                  value={selectedOperationId || ''}
                  onChange={handleOperationChange}
                  label="Operation"
                  required
                >
                  <MenuItem value="">
                    <em>Select an Operation</em>
                  </MenuItem>
                  {filteredOperations.map((operation) => (
                    <MenuItem key={operation.id} value={operation.id}>
                      {operation.operation_num} - {operation.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.some(error => error.includes('Operation')) && (
                  <FormHelperText>Operation is required</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {/* Selected Work Order Info */}
          {selectedWorkOrder && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                <strong>Selected Work Order:</strong> {selectedWorkOrder.wo_num}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Description:</strong> {selectedWorkOrder.part?.description || 'No description'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Quantity:</strong> {selectedWorkOrder.qty || 'N/A'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Serial Selection */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.cardHeader}>
            <SerialIcon className={classes.cardIcon} />
            <Typography variant="h6">Failed Serial Selection</Typography>
          </Box>
          
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Select the serial numbers that have failed and need to be reported in this NCR.
          </Typography>

          {!selectedWorkOrderId || !selectedOperationId ? (
            <Box className={classes.noSerials}>
              <Typography variant="body2">
                Please select a Work Order and Operation to view available serials.
              </Typography>
            </Box>
          ) : availableSerials.length === 0 ? (
            <Box className={classes.noSerials}>
              <Typography variant="body2">
                No serials found for the selected Work Order and Operation.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                Available Serials ({availableSerials.length}):
              </Typography>
              <Box className={classes.serialChips}>
                {availableSerials.map((serial) => (
                  <Chip
                    key={serial.id}
                    label={serial.serial_num}
                    clickable
                    color={selectedSerials.includes(serial.serial_num) ? 'primary' : 'default'}
                    onClick={() => handleSerialToggle(serial.serial_num)}
                    variant={selectedSerials.includes(serial.serial_num) ? 'default' : 'outlined'}
                  />
                ))}
              </Box>
              
              {selectedSerials.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2">
                    Selected Failed Serials ({selectedSerials.length}):
                  </Typography>
                  <Box className={classes.serialChips}>
                    {selectedSerials.map((serialNum) => (
                      <Chip
                        key={serialNum}
                        label={serialNum}
                        color="secondary"
                        onDelete={() => handleSerialToggle(serialNum)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
          
          {validationErrors.some(error => error.includes('serial')) && (
            <Typography className={classes.validationError}>
              At least one serial must be selected
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#ffebee' }}>
          <Typography variant="subtitle2" color="error" gutterBottom>
            Please fix the following errors:
          </Typography>
          {validationErrors.map((error, index) => (
            <Typography key={index} variant="body2" color="error">
              • {error}
            </Typography>
          ))}
        </Paper>
      )}

      {/* Summary */}
      {selectedWorkOrder && selectedOperation && selectedSerials.length > 0 && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              NCR Summary
            </Typography>
            <Typography variant="body2">
              <strong>Work Order:</strong> {selectedWorkOrder.wo_num}
            </Typography>
            <Typography variant="body2">
              <strong>Operation:</strong> {selectedOperation.operation_num} - {selectedOperation.name}
            </Typography>
            <Typography variant="body2">
              <strong>Failed Serials:</strong> {selectedSerials.length} serial(s)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedSerials.join(', ')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={createNCRLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={createNCRLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={createNCRLoading || !selectedWorkOrderId || !selectedOperationId || selectedSerials.length === 0}
        >
          {createNCRLoading ? 'Creating...' : 'Create NCR Report'}
        </Button>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!createNCRError}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={createNCRError}
      />
    </Box>
  );
};

export default NCREdit; 